import { useEffect, useSyncExternalStore } from "react";
import { createNotificationService } from "./services/notificationService";

/**
 * One unread counter shared by the top-bar bell and the Notifications page, so marking a
 * notification as read updates the bell immediately. Source of truth is GET /notify/getCount.
 */
const POLL_MS = 60000;

let unreadCount = 0;
const listeners = new Set();

const emit = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getSnapshot = () => unreadCount;

export const setUnreadCount = (value) => {
  const next = Math.max(0, Number(value) || 0);
  if (next !== unreadCount) {
    unreadCount = next;
    emit();
  }
};

export const adjustUnreadCount = (delta) => setUnreadCount(unreadCount + delta);

export const useUnreadCount = () => useSyncExternalStore(subscribe, getSnapshot);

/** Keeps the shared counter fresh: on mount, every 60 seconds and when the tab regains focus. */
export const useUnreadCountSync = (httpClient, enabled = true) => {
  useEffect(() => {
    if (!enabled || !httpClient) return undefined;
    const service = createNotificationService(httpClient);
    let cancelled = false;

    const refresh = () => {
      if (document.hidden) return;
      service
        .getUnreadCount()
        .then((count) => {
          if (!cancelled) setUnreadCount(count);
        })
        .catch(() => {});
    };

    refresh();
    const timer = window.setInterval(refresh, POLL_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [httpClient, enabled]);
};
